import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ProductVariant } from '@/schemas/variants';

interface VariantsState {
  byId: Record<string, ProductVariant>;
  allIds: string[];
  filters: {
    collaboratorId?: string;
    productId?: string;
    isActive?: boolean;
  };
  selectedVariantId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: VariantsState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedVariantId: null,
  isLoading: false,
  error: null,
};

const variantsSlice = createSlice({
  name: 'variants',
  initialState,
  reducers: {
    setVariants: (state, action: PayloadAction<ProductVariant[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((variant) => {
        state.byId[variant.id] = variant;
        state.allIds.push(variant.id);
      });
    },
    addVariant: (state, action: PayloadAction<ProductVariant>) => {
      const variant = action.payload;
      state.byId[variant.id] = variant;
      if (!state.allIds.includes(variant.id)) {
        state.allIds.push(variant.id);
      }
    },
    updateVariant: (state, action: PayloadAction<ProductVariant>) => {
      const variant = action.payload;
      state.byId[variant.id] = variant;
    },
    deleteVariant: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedVariantId: (state, action: PayloadAction<string | null>) => {
      state.selectedVariantId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<VariantsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setVariants,
  addVariant,
  updateVariant,
  deleteVariant,
  setSelectedVariantId,
  setFilters,
  setLoading,
  setError,
} = variantsSlice.actions;

export default variantsSlice.reducer;
